import {
  Injectable,
  inject
} from '@angular/core';

import {
  HttpErrorResponse
} from '@angular/common/http';

// ======================================================
// RXJS
// ======================================================

import {
  firstValueFrom
} from 'rxjs';

// ======================================================
// SERVICES
// ======================================================

import { HttpService } from '../http/http.service';
import { AlertService } from './alert.service';
import { LoaderService } from './loader.service';

// ======================================================
// ENUMS
// ======================================================

import { ResponseCode } from '../enums/responsecode.enum';

// ======================================================
// CONSTANTS
// ======================================================

import {
  INITIATE_UPLOAD_URL,
  UPLOAD_VIDEO_URL
} from '../constants/apiEndpoints.constants';

// ======================================================
// TYPES
// ======================================================

interface UploadChunk {
  index: number;
  chunk: Blob;
  isUploading: boolean;
  isUploaded: boolean;
}

interface UploadSource {
  blob?: Blob;
  size?: number;
  readableSize?: string;
  uploadId: string;
  fileName?: string;
}

interface UploadConfig {
  uuid: string;
  source: UploadSource;
  totalChunks: number;
  chunkSize: number;
  chunks: UploadChunk[];
}

interface InitiateUploadResult {
  success: boolean;
  uploadId?: string;
  error?: HttpErrorResponse;
  response?: any;
}

@Injectable({
  providedIn: 'root'
})
export class UploaderService {

  // ======================================================
  // DI
  // ======================================================

  private readonly http =
    inject(HttpService);

  private readonly alertService =
    inject(AlertService);

  private readonly loaderSrv =
    inject(LoaderService);

  // ======================================================
  // STATE
  // ======================================================

  readonly uploadList:
    UploadConfig[] = [];

  // ======================================================
  // BEGIN VIDEO UPLOAD
  // ======================================================

async beginVideoUpload(
  uuid: string,
  file: File,
  data: any,
  successCB?: (
    response: any
  ) => void,
  errorCB?: (
    error: any
  ) => void,
  fileName?: string,
  loadInBackground = false
): Promise<void> {

  this.uploadList.push({
    uuid,
    source: {
      uploadId: ''
    },
    totalChunks: 0,
    chunkSize:
      10 * 1024 * 1024,
    chunks: []
  });

  try {

    const localConfig =
      this.getUploadConfig(uuid);

    if (!localConfig) {
      return;
    }

    const chunks =
      await this.uploadPreprocessing(
        uuid,
        file,
        data,
        successCB,
        fileName
      );

    if (!chunks.length) {

      errorCB?.({
        message:
          'No chunks generated'
      });

      return;
    }

    this.loaderSrv
      .updateProgressCount(0);

const uploadResult =
  await this.initiateUpload(
    uuid,
    loadInBackground
  );

if (!uploadResult.success) {

  errorCB?.(
    uploadResult.error ??
    uploadResult.response ??
    {
      message:
        'Failed to initialize upload'
    }
  );

  return;
}

const uploadId =
  uploadResult.uploadId;

if (
  !uploadId ||
  !localConfig.source.uploadId
) {

  errorCB?.({
    message:
      'Upload Id not returned'
  });

  return;
}

    let uploadFailed = false;

    for (
      let index = 0;
      index < chunks.length;
      index++
    ) {

      const currentChunk =
        chunks[index];

      const previousChunk =
        index === 0
          ? null
          : chunks[index - 1];

      const uploaded =
        await this.uploadChunkWithRetry(
          uuid,
          previousChunk,
          currentChunk,
          loadInBackground,
          4 // initial + 3 retries
        );

      if (!uploaded) {

        console.error(
          `Chunk ${
            currentChunk.index + 1
          } permanently failed`
        );

        uploadFailed = true;

        break;
      }

      this.loaderSrv
        .updateProgressCount(
          Math.round(
            (
              (index + 1) /
              chunks.length
            ) * 100
          )
        );
    }

    if (uploadFailed) {

      errorCB?.({
        message:
          'Upload failed after multiple retry attempts',
        uploadId:
          localConfig.source.uploadId,
        ...data
      });

      return;
    }

    successCB?.({
      uploadId:
        localConfig.source.uploadId,
      ...data
    });

  } catch (error) {

    console.error(
      'beginVideoUpload failed',
      error
    );

    errorCB?.(error);

  } finally {

    this.removeUpload(
      uuid
    );

    this.loaderSrv
      .resetProgress();
  }
}

private async uploadChunkWithRetry(
  uuid: string,
  previousChunk:
    UploadChunk | null,
  currentChunk: UploadChunk,
  loadInBackground = false,
  maxRetries = 4
): Promise<boolean> {

  for (
    let attempt = 1;
    attempt <= maxRetries;
    attempt++
  ) {

    const uploaded =
      await this.initiateFileUpload(
        uuid,
        previousChunk,
        currentChunk,
        loadInBackground
      );

    if (uploaded) {

      return true;
    }

    console.warn(
      `Chunk ${
        currentChunk.index + 1
      } failed.
      Attempt ${attempt}/${maxRetries}`
    );

    if (
      attempt < maxRetries
    ) {

      await new Promise(
        resolve =>
          setTimeout(
            resolve,
            attempt * 1000
          )
      );
    }
  }

  return false;
}

  // ======================================================
  // GET CONFIG
  // ======================================================

  getUploadConfig(
    uuid: string
  ): UploadConfig | undefined {

    return this.uploadList.find(
      upload => upload.uuid === uuid
    );
  }

  // ======================================================
  // PREPROCESS FILE
  // ======================================================

  async uploadPreprocessing(
    uuid: string,
    file: File,
    data: any,
    successCB?: Function,
    fileName?: string
  ): Promise<UploadChunk[]> {

    const localConfig =
      this.getUploadConfig(uuid);

    if (!localConfig) {
      return [];
    }

    // localConfig.source.blob =
    //   blob;

    // localConfig.source.size =
    //   blob.size;

    localConfig.source.size =
  file.size;

    // localConfig.source.readableSize =
    //   this.parseFileSize(blob.size);
    localConfig.source.readableSize =
  this.parseFileSize(file.size);

localConfig.totalChunks =
  Math.ceil(
    file.size /
    localConfig.chunkSize
  );

    let generatedFileName =
      data.studyId &&
      data.stimuliId
        ? `study_${data.studyId}_stimuli_${data.stimuliId}`
        : 'default-file-name';

    // generatedFileName =
    //   fileName
    //     ? fileName
    //     : `${generatedFileName}.${blob.type.split('/')[1]}`;

    // const file =
    //   new File(
    //     [blob],
    //     generatedFileName
    //   );

    // localConfig.source.fileName =
    //   file.name;

    localConfig.source.fileName =
  fileName ??
  file.name;

    for (
      let i = 0;
      i < localConfig.totalChunks;
      i++
    ) {

      const offset =
        i * localConfig.chunkSize;

      const chunk =
        file.slice(
          offset,
          offset +
          localConfig.chunkSize
        );

      localConfig.chunks.push({
        index: i,
        chunk,
        isUploading: false,
        isUploaded: false
      });
    }

    return localConfig.chunks;
  }

  // ======================================================
  // FILE SIZE FORMATTER
  // ======================================================

  parseFileSize(
    size: number
  ): string {

    const units = [
      'bytes',
      'KB',
      'MB',
      'GB',
      'TB'
    ];

    let i = 0;
    let n = size;

    while (
      n >= 1024 &&
      ++i
    ) {
      n = n / 1024;
    }

    return (
      n.toFixed(i < 10 ? 1 : 0) +
      ' ' +
      units[i]
    );
  }

  // ======================================================
  // INITIATE UPLOAD
  // ======================================================

async initiateUpload(
  uuid: string,
  loadInBackground = false
): Promise<InitiateUploadResult> {

  const localConfig =
    this.getUploadConfig(uuid);

  if (!localConfig) {

    return {
      success: false
    };
  }

  try {

    const url =
      INITIATE_UPLOAD_URL(
        localConfig.source.fileName ?? '',
        String(
          localConfig.source.size ?? 0
        )
      );

    const response: any =
      await firstValueFrom(
        this.http.get(
          url,
          {},
          {
            loadInBackground
          }
        )
      );

if (
  response?.code !==
  ResponseCode.Ok
) {

  return {
    success: false,
    response
  };
}

    const uploadId =
      response?.data?.upload_id;

    if (!uploadId) {

      return {
        success: false
      };
    }

    localConfig.source.uploadId =
      uploadId;

    return {

      success: true,

      uploadId
    };

  } catch (error) {

    return {

      success: false,

      error:
        error as HttpErrorResponse
    };
  }
}

  // ======================================================
  // INITIATE FILE UPLOAD
  // ======================================================

  async initiateFileUpload(
    uuid: string,
    previousChunk:
      UploadChunk | null,
    currentChunk: UploadChunk,
    loadInBackground = false
  ): Promise<boolean> {

    return new Promise(resolve => {

      if (
        previousChunk === null ||
        (
          !previousChunk.isUploading &&
          previousChunk.isUploaded
        )
      ) {

        this.uploadFile(
          uuid,
          currentChunk.chunk,
          currentChunk,
          () => {

            currentChunk.isUploading =
              false;

            currentChunk.isUploaded =
              true;

            resolve(true);
          },
          (
            error:
              HttpErrorResponse
          ) => {

            this.alertService.error(
              error.error
            );

            resolve(false);
          },
          loadInBackground
        );
      }
    });
  }

  // ======================================================
  // UPLOAD FILE
  // ======================================================

  uploadFile(
    uuid: string,
    blob: Blob,
    data: UploadChunk,
    successCB: (
      response: any
    ) => void,
    errorCB: (
      error:
        HttpErrorResponse
    ) => void,
    loadInBackground = false
  ): void {

    const localConfig =
      this.getUploadConfig(uuid);

    if (!localConfig) {
      return;
    }

    const url =
      UPLOAD_VIDEO_URL(
        localConfig.source.uploadId,
        data.index ===
        localConfig.chunks.length - 1
      );

    this.http.post(
      url,
      blob,
      {},
      {
        loadInBackground
      }
    ).subscribe({
      next: response => {
        successCB(response);
      },
      error: (
        error:
          HttpErrorResponse
      ) => {
        errorCB(error);
      }
    });
  }

  private removeUpload(
  uuid: string
): void {

  const index =
    this.uploadList.findIndex(
      upload =>
        upload.uuid === uuid
    );

  if (index > -1) {

    this.uploadList.splice(
      index,
      1
    );
  }
}
}